import { ownerStorage, getStorageOwner } from '../privacy/ownerStorage';
/**
 * Consent Management Service
 *
 * Handles consent state persistence, parental consent flows,
 * and consent validation at runtime.
 */

import type {
  ConsentScope,
  UserConsentState,
  ConsentGrant,
  ConsentPolicy,
  ConsentCheckResult,
  ConsentEvent,
  AgeGroup,
  ParentalConsentStatus,
} from './types';
import {
  DEFAULT_CONSENT_POLICY,
  checkConsent,
  canGrantConsent,
  getAgeGroup,
  DATA_CLASSIFICATION,
} from './types';

// ============================================
// STORAGE KEYS
// ============================================

const STORAGE_KEYS = {
  CONSENT_STATE: 'resilience_consent_state',
  CONSENT_EVENTS: 'resilience_consent_events',
} as const;

// ============================================
// CONSENT SERVICE CLASS
// ============================================

export class ConsentService {
  private initialOwner = getStorageOwner();
  private get storageOwner() {const current=getStorageOwner();return current.userId===this.initialOwner.userId && current.tenantId===this.initialOwner.tenantId?current:this.initialOwner;}
  private state: UserConsentState | null = null;
  private policy: ConsentPolicy;
  private tenantId: string;
  private eventListeners: Set<(event: ConsentEvent) => void> = new Set();

  constructor(tenantId: string, policy?: ConsentPolicy) {
    this.tenantId = tenantId;
    this.policy = policy ?? DEFAULT_CONSENT_POLICY;
  }

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  /**
   * Initialize consent service and load persisted state
   */
  async initialize(userId: string): Promise<UserConsentState> {
    // Try to load from storage
    const stored = ownerStorage.getItem(STORAGE_KEYS.CONSENT_STATE, this.storageOwner);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as UserConsentState;
        if (parsed.userId === userId && parsed.tenantId === this.tenantId) {
          this.state = parsed;
          return this.state;
        }
      } catch {
        console.warn('[ConsentService] Failed to parse stored consent state');
      }
    }

    // Create new state
    this.state = this.createDefaultState(userId);
    this.persist();
    return this.state;
  }

  /**
   * Create default consent state for new user
   */
  private createDefaultState(userId: string): UserConsentState {
    const now = new Date().toISOString();

    return {
      userId,
      tenantId: this.tenantId,
      ageVerification: {
        ageGroup: '13_to_17', // Default, will be updated during onboarding
        verifiedAt: '',
        method: 'self_reported',
      },
      parentalConsent: {
        status: 'not_required',
      },
      grants: [], // No grants by default - user must opt in
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
  }

  /**
   * Persist state to localStorage
   */
  private persist(): void {
    if (this.state) {
      ownerStorage.setItem(STORAGE_KEYS.CONSENT_STATE, JSON.stringify(this.state), this.storageOwner);
    }
  }

  /**
   * Get current consent state (readonly)
   */
  getState(): UserConsentState | null {
    return this.state ? { ...this.state } : null;
  }

  /**
   * Get current policy
   */
  getPolicy(): ConsentPolicy {
    return { ...this.policy };
  }

  // ============================================
  // AGE VERIFICATION
  // ============================================

  /**
   * Set user's age group (during onboarding)
   */
  async setAgeGroup(
    birthYear: number,
    method: 'self_reported' | 'parent_verified' | 'institution_provided' = 'self_reported'
  ): Promise<void> {
    if (!this.state) {
      throw new Error('ConsentService not initialized');
    }

    if (!Number.isInteger(birthYear) || birthYear > new Date().getFullYear() || birthYear < new Date().getFullYear() - 120) throw new Error('Invalid age range');
    await this.setAgeBand(getAgeGroup(birthYear), method);
  }

  async setAgeBand(ageGroup: AgeGroup, method: 'self_reported' | 'parent_verified' | 'institution_provided' = 'self_reported'): Promise<void> {
    if (!this.state) throw new Error('ConsentService not initialized');
    if (method !== 'self_reported') throw new Error('Verified enrollment must be provided by the server');
    const now = new Date().toISOString();

    this.state.ageVerification = {
      ageGroup,
      verifiedAt: now,
      method,
      // Note: birthYear is NOT stored for privacy
    };

    // Update parental consent requirement
    if (ageGroup === 'under_13') {
      this.state.parentalConsent = {
        status: 'pending',
        requestedAt: now,
      };
    } else {
      this.state.parentalConsent = {
        status: 'not_required',
      };
    }

    // Set default grants based on age
    const defaultScopes = this.policy.defaultScopes[ageGroup];
    for (const scope of defaultScopes) {
      if (canGrantConsent(this.state, scope, this.policy)) {
        await this.grantConsent(scope, 'user');
      }
    }

    this.state.updatedAt = now;
    this.state.version++;
    this.persist();

    this.emitEvent({
      type: 'age_verified',
      userId: this.state.userId,
      tenantId: this.tenantId,
      metadata: { ageGroup, method },
      timestamp: now,
    });
  }

  /**
   * Check if user needs parental consent
   */
  needsParentalConsent(): boolean {
    if (!this.state) return true;
    return (
      this.state.ageVerification.ageGroup === 'under_13' &&
      this.state.parentalConsent.status === 'pending'
    );
  }

  // ============================================
  // PARENTAL CONSENT FLOW
  // ============================================

  /**
   * Request parental consent via email
   */
  async requestParentalConsent(_parentEmail: string): Promise<string> {
    throw new Error('Verified guardian enrollment is not configured. Do not enter a child’s private data. Use the public practice and support options.');
  }

  async verifyParentalConsent(_code: string): Promise<boolean> {
    return false; // A browser code cannot prove guardian consent.
  }

  /**
   * Deny parental consent
   */
  async denyParentalConsent(): Promise<void> {
    if (!this.state) {
      throw new Error('ConsentService not initialized');
    }

    const now = new Date().toISOString();

    this.state.parentalConsent = {
      ...this.state.parentalConsent,
      status: 'denied',
      respondedAt: now,
      verificationCode: undefined,
    };

    // Revoke any scopes that require parental consent
    for (const grant of this.state.grants) {
      const scopeInfo = await import('./types').then(m => m.CONSENT_SCOPE_INFO[grant.scope]);
      if (scopeInfo?.requiresParentalConsent && grant.granted) {
        await this.revokeConsent(grant.scope);
      }
    }

    this.state.updatedAt = now;
    this.state.version++;
    this.persist();

    this.emitEvent({
      type: 'parental_consent_denied',
      userId: this.state.userId,
      tenantId: this.tenantId,
      timestamp: now,
    });
  }

  // ============================================
  // CONSENT GRANTS
  // ============================================

  /**
   * Grant consent for a specific scope
   */
  async grantConsent(
    scope: ConsentScope,
    source: 'user' | 'parent' | 'institution_default' = 'user'
  ): Promise<ConsentCheckResult> {
    if (!this.state) {
      throw new Error('ConsentService not initialized');
    }

    // Check if user can grant this consent
    if (!canGrantConsent(this.state, scope, this.policy)) {
      return {
        allowed: false,
        reason: 'age_restricted',
        requiredAction: 'await_parent',
      };
    }

    const now = new Date().toISOString();

    // Find existing grant or create new
    const existingIndex = this.state.grants.findIndex(g => g.scope === scope);
    const grant: ConsentGrant = {
      scope,
      granted: true,
      grantedAt: now,
      source,
    };

    if (existingIndex >= 0) {
      this.state.grants[existingIndex] = grant;
    } else {
      this.state.grants.push(grant);
    }

    this.state.updatedAt = now;
    this.state.version++;
    this.persist();

    this.emitEvent({
      type: 'consent_granted',
      userId: this.state.userId,
      tenantId: this.tenantId,
      scope,
      metadata: { source },
      timestamp: now,
    });

    return { allowed: true };
  }

  /**
   * Revoke consent for a specific scope
   */
  async revokeConsent(scope: ConsentScope): Promise<void> {
    if (!this.state) {
      throw new Error('ConsentService not initialized');
    }

    // Check if scope is required
    if (this.policy.requiredScopes.includes(scope)) {
      throw new Error(`Cannot revoke required scope: ${scope}`);
    }

    const now = new Date().toISOString();

    const existingIndex = this.state.grants.findIndex(g => g.scope === scope);
    if (existingIndex >= 0) {
      this.state.grants[existingIndex] = {
        ...this.state.grants[existingIndex],
        granted: false,
        revokedAt: now,
      };
    }

    this.state.updatedAt = now;
    this.state.version++;
    this.persist();

    this.emitEvent({
      type: 'consent_revoked',
      userId: this.state.userId,
      tenantId: this.tenantId,
      scope,
      timestamp: now,
    });
  }

  /**
   * Revoke all consent and request data deletion
   */
  async revokeAllConsent(): Promise<void> {
    if (!this.state) {
      throw new Error('ConsentService not initialized');
    }

    if (!this.policy.allowFullRevocation) {
      throw new Error('Full revocation is not allowed by tenant policy');
    }

    const now = new Date().toISOString();

    // Revoke all non-required grants
    for (const grant of this.state.grants) {
      if (!this.policy.requiredScopes.includes(grant.scope)) {
        grant.granted = false;
        grant.revokedAt = now;
      }
    }

    this.state.updatedAt = now;
    this.state.version++;
    this.persist();

    this.emitEvent({
      type: 'full_data_deletion_requested',
      userId: this.state.userId,
      tenantId: this.tenantId,
      timestamp: now,
    });
  }

  // ============================================
  // CONSENT CHECKS
  // ============================================

  /**
   * Check if user has consent for a scope
   */
  hasConsent(scope: ConsentScope): ConsentCheckResult {
    if (!this.state) {
      return {
        allowed: false,
        reason: 'no_consent',
        requiredAction: 'request_consent',
      };
    }

    return checkConsent(this.state, scope, this.policy);
  }

  /**
   * Check if user can process a specific data type
   */
  canProcessData(dataType: keyof typeof DATA_CLASSIFICATION): ConsentCheckResult {
    const classification = DATA_CLASSIFICATION[dataType];
    if (!classification) {
      return { allowed: false, reason: 'no_consent' };
    }

    return this.hasConsent(classification.minScope);
  }

  /**
   * Get all granted scopes
   */
  getGrantedScopes(): ConsentScope[] {
    if (!this.state) return [];

    return this.state.grants
      .filter(g => g.granted)
      .filter(g => this.hasConsent(g.scope).allowed)
      .map(g => g.scope);
  }

  /**
   * Get user's age group
   */
  getAgeGroup(): AgeGroup | null {
    return this.state?.ageVerification.ageGroup ?? null;
  }

  /**
   * Get parental consent status
   */
  getParentalConsentStatus(): ParentalConsentStatus {
    return this.state?.parentalConsent.status ?? 'not_required';
  }

  // ============================================
  // UTILITIES
  // ============================================

  // ============================================
  // EVENT HANDLING
  // ============================================

  /**
   * Subscribe to consent events
   */
  onConsentEvent(listener: (event: ConsentEvent) => void): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  /**
   * Emit consent event to listeners and persist
   */
  private emitEvent(event: ConsentEvent): void {
    // Notify listeners
    for (const listener of this.eventListeners) {
      try {
        listener(event);
      } catch (err) {
        console.error('[ConsentService] Event listener error:', err);
      }
    }

    // Persist to event log
    this.persistEvent(event);
  }

  /**
   * Persist event to local storage (audit trail)
   */
  private persistEvent(event: ConsentEvent): void {
    try {
      const stored = ownerStorage.getItem(STORAGE_KEYS.CONSENT_EVENTS, this.storageOwner);
      const events: ConsentEvent[] = stored ? JSON.parse(stored) : [];

      events.push(event);

      // Keep last 100 events
      if (events.length > 100) {
        events.splice(0, events.length - 100);
      }

      ownerStorage.setItem(STORAGE_KEYS.CONSENT_EVENTS, JSON.stringify(events), this.storageOwner);
    } catch (err) {
      console.error('[ConsentService] Failed to persist event:', err);
    }
  }

  /**
   * Get consent event history
   */
  getEventHistory(): ConsentEvent[] {
    try {
      const stored = ownerStorage.getItem(STORAGE_KEYS.CONSENT_EVENTS, this.storageOwner);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  // ============================================
  // CLEANUP
  // ============================================

  /**
   * Clear all consent data (for logout/account deletion)
   */
  clearAllData(): void {
    ownerStorage.removeItem(STORAGE_KEYS.CONSENT_STATE, this.storageOwner);
    ownerStorage.removeItem(STORAGE_KEYS.CONSENT_EVENTS, this.storageOwner);
    this.state = null;
    this.eventListeners.clear();
  }
}

// ============================================
// SINGLETON INSTANCE FACTORY
// ============================================

let instance: ConsentService | null = null;

/**
 * Get or create the consent service instance
 */
export function getConsentService(tenantId: string, policy?: ConsentPolicy): ConsentService {
  if (!instance || instance['tenantId'] !== tenantId) {
    instance = new ConsentService(tenantId, policy);
  }
  return instance;
}

/**
 * Reset the consent service (for testing)
 */
export function resetConsentService(): void {
  instance = null;
}

